import { apiErrorField } from '@agos/api-client'
import {
  getGetStockItemsQueryKey,
  getGetStockMovementsQueryKey,
  getGetStockItemCategoriesQueryKey,
  useGetStockCategories,
  useGetStockItemCategories,
  useDeleteStockItemCategoriesId,
  usePatchStockItemCategoriesId,
  usePostStockItemCategories,
  useGetStockItems,
  useGetStockMargins,
  useGetStockMovements,
  usePatchStockItemsId,
  usePostStockItems,
  usePostStockMovements,
} from '@agos/api-client/inventory'
import { T, formatCents, formatDateTime, tt, useAuth, useModalFocus } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  DateRangePicker,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
  ReadFailedBlock,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileUp,
  Info,
  Package,
  Plus,
  RotateCcw,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { mayReadPhotoFor } from '../inventory-rights'

import { GoodsReceiptDialog } from './GoodsReceiptDialog'
import {
  categoriesOf,
  idsToRename,
  isBlocking,
  matchingCategory,
  outcomeNote,
  planNote,
  renamePayload,
  categoryRowFor,
  manageableCategories as manageableCategoriesOf,
  reorderPlan,
  renameProblems,
} from './category-rename'

import {
  formatPercent,
  itemMargin,
  marginCaveat,
  marginsById,
  stockValue,
  type MarginRow,
} from './margins'
import {
  byUrgency,
  costUnitOf,
  filterItems,
  movementCapNote,
  levelColor,
  levelCounts,
  levelLabel,
  movementColor,
  movementLabel,
  signedQty,
  stockLevel,
  unitOptionsFor,
  usedCategories,
  type StockItem,
  type StockMovement,
} from './stock-levels'
import {
  canPost,
  canSave,
  checkForm,
  customUnitNote,
  formOf,
  itemPayload,
  movementPayload,
  overdrawWarning,
  resultingQty,
  type ItemForm,
  type MovementForm,
  type MovementKind,
} from './stock-payloads'

/**
 * `/stock` — what is in the stock room, and every movement in and out of it.
 *
 * Legacy's two tabs, its filters, its item editor and its quick receive/waste
 * actions. Three things it does not do:
 *
 *  - **FB-077.** The cost-unit select offers ten units and nothing else, so the four
 *    items priced per `fľaša` have `ks` selected without anybody choosing it — and
 *    saving writes it. The item's *own* unit select already guards against this three
 *    lines above; the cost one does not.
 *  - **FB-078.** The margin header sums cost over one set of items and revenue over
 *    another and subtracts. On the real data that reports a profit of −28,70 € beside
 *    an average margin of 0 %.
 *  - **99 of the 135 items are below their minimum**, so a badge on three rows in
 *    four says nothing. The list is ordered worst-first.
 *
 * Ported from legacy `pages/stock.tsx`.
 */

/* §9.628 - the same figure and the same reason as `SettingsTranslationsPage`: the movements search runs
 * on the server now, and a request per keystroke is a count over the whole table for every letter. */
const MOVEMENT_SEARCH_DEBOUNCE_MS = 350

type StockTab = 'items' | 'movements'

export function StockPage() {
  const [tab, setTab] = useState<StockTab>('items')
  const [typeFilter, setTypeFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  // §9.136 — deactivated rows are hidden by default; this is the way back to them.
  const [includeInactive, setIncludeInactive] = useState(false)

  const [movType, setMovType] = useState('all')
  const [movCategory, setMovCategory] = useState('all')
  const [movFrom, setMovFrom] = useState('')
  const [movTo, setMovTo] = useState('')
  const [movSearch, setMovSearch] = useState('')

  const [editing, setEditing] = useState<{ item: StockItem | null } | null>(null)
  const [receiptOpen, setReceiptOpen] = useState(false)
  // §9.758 - the receipt dialog's photo reading asks `stock_edit` alone (BACKEND.md §10A.413): it is metered.
  const { hasPermission } = useAuth()
  const mayReadPhoto = mayReadPhotoFor(hasPermission)
  const [renaming, setRenaming] = useState<{ from: string; to: string } | null>(null)
  const [newCategory, setNewCategory] = useState('')
  const [categoryBusy, setCategoryBusy] = useState(false)
  const [renameBusy, setRenameBusy] = useState(false)
  const queryClientTop = useQueryClient()
  const renameItem = usePatchStockItemsId()
  const renameCategory = usePatchStockItemCategoriesId()
  const addCategoryCall = usePostStockItemCategories()
  const deleteCategoryCall = useDeleteStockItemCategoriesId()

  const addCategory = async () => {
    setCategoryBusy(true)
    try {
      await addCategoryCall.mutateAsync({ data: { name: newCategory.trim() } })
      setNewCategory('')
      refreshCategories()
    } catch {
      // The server refuses a duplicate by name and says so; surfacing its own words beats guessing.
      notify({ variant: 'destructive', title: tt('Kategóriu sa nepodarilo pridať.') })
    } finally {
      setCategoryBusy(false)
    }
  }

  const removeCategory = async (id: number) => {
    setCategoryBusy(true)
    try {
      await deleteCategoryCall.mutateAsync({ id })
      refreshCategories()
    } catch {
      notify({ variant: 'destructive', title: tt('Kategóriu sa nepodarilo zmazať.') })
    } finally {
      setCategoryBusy(false)
    }
  }

  const moveCategory = async (index: number, delta: number) => {
    const plan = reorderPlan(manageableCategories, index, delta)
    if (plan.length === 0) return
    setCategoryBusy(true)
    try {
      for (const step of plan) {
        await renameCategory.mutateAsync({ id: step.id, data: { sortOrder: step.sortOrder } })
      }
      refreshCategories()
    } catch {
      notify({ variant: 'destructive', title: tt('Poradie sa nepodarilo zmeniť.') })
    } finally {
      setCategoryBusy(false)
    }
  }
  const { toast: notify } = useToast()
  const [moving, setMoving] = useState<{ item: StockItem; type: MovementKind } | null>(null)

  /* FB-838, §9.552 - **`isError`, because four figures and a caveat come off this one read.** `items` is
     `itemData ?? []`, so a failure gave `levelCounts([])` and the *Kritických* card rendered `0` in **emerald** - the
     colour this screen uses for *nothing needs attention* - while this file's own javadoc records the real answer on the
     owner's data: 99 of 135 items below their minimum. */
  const { data: itemData, isLoading, isError: itemsFailed, refetch: refetchItems } = useGetStockItems()
  const { data: categoryData } = useGetStockCategories()
  // §10A.16 — the categories as rows, so a rename is one call rather than one per item.
  const { data: categoryRows } = useGetStockItemCategories()
  const { data: marginData } = useGetStockMargins()

  const items = useMemo<StockItem[]>(() => itemData ?? [], [itemData])

  // §10A.16 — the rows the table holds, plus any name only the items know about. Not wrapped in
  // useMemo: the React Compiler refuses one it cannot preserve here, and it memoizes this itself.
  const manageableCategories = manageableCategoriesOf(categoryRows ?? [], items)

  const refreshCategories = () => {
    void queryClientTop.invalidateQueries({ queryKey: getGetStockItemCategoriesQueryKey() })
    void queryClientTop.invalidateQueries({ queryKey: getGetStockItemsQueryKey() })
  }

  const margins = useMemo(() => marginsById((marginData ?? []) as MarginRow[]), [marginData])
  const storedCategories = useMemo<string[]>(() => categoryData ?? [], [categoryData])

  const shown = useMemo(
    () =>
      byUrgency(
        filterItems(items, {
          type: typeFilter,
          category: categoryFilter,
          search,
          includeInactive,
        }),
      ),
    [items, typeFilter, categoryFilter, search, includeInactive],
  )
  const counts = levelCounts(items)
  const value = stockValue(items, margins)
  const caveat = marginCaveat(value)
  const categories = useMemo(() => {
    const inUse = usedCategories(items)
    return [...new Set([...inUse, ...storedCategories])].sort((left, right) =>
      left.localeCompare(right, 'sk'),
    )
  }, [items, storedCategories])

  /* §9.628 - 350 ms after typing stops, which is `SettingsTranslationsPage`'s own figure and its reason:
   * this search reaches the server now, and a keystroke per request is a `count(*)` over 111 881 rows
   * the server did not need. */
  const [movSearchSent, setMovSearchSent] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setMovSearchSent(movSearch.trim()), MOVEMENT_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [movSearch])

  /* §9.628 - **both of the screen's narrowings go to the server**. They used to run in the browser over
   * whatever `MOVEMENT_LIMIT` had sent, so a search or a category older than the newest 500 rows read as
   * *nothing here*. The type and the dates were already server-side; these two join them. */
  /* §9.632, FB-947 - **the second read on this screen, and the census cannot see it.** FB-838 converted the
     items read, so this file mentions `isError` and a count of files that never do can never reach here.
     `MovementTable`'s `emptyMessage` is *Žiadne pohyby*, so a failed read said *no movements* about a
     warehouse holding 111 895 of them. */
  const {
    data: movementData,
    isLoading: movementsLoading,
    isError: movementsFailed,
    refetch: refetchMovements,
  } = useGetStockMovements(
    {
      ...(movType === 'all' ? {} : { type: movType }),
      ...(movFrom ? { date_from: movFrom } : {}),
      ...(movTo ? { date_to: movTo } : {}),
      ...(movSearchSent === '' ? {} : { search: movSearchSent }),
      ...(movCategory === 'all' ? {} : { category: movCategory }),
    },
    { query: { enabled: tab === 'movements' } },
  )
  const movements = (movementData?.rows ?? []) as StockMovement[]
  // Off the server's own count rather than off `movements.length`, which cannot tell a short answer
  // from a truncated one - §3.48's rule and §10A.317's reason for answering a total at all.
  const movementNote = movementCapNote(movements.length, movementData?.total ?? 0)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Zásoby')}
        description={tt('Stav skladu, príjem, výdaj a odpisy')}
        icon={<Package className="w-5 h-5" />}
        actions={
          /* §9.268 — `flex-wrap`, because `PageHeader` cannot wrap what a caller has bound together
           * in a row of its own. Three buttons here reached 511 px inside a 390 px phone even after
           * the header itself learned to wrap. */
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-open-rename
              onClick={() => setRenaming({ from: '', to: '' })}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border font-bold hover:bg-muted transition-colors text-sm"
            >
              <Tag className="w-4 h-4" />
              <T>Premenovať kategóriu</T>
            </button>
            <button
              type="button"
              data-open-receipt
              onClick={() => setReceiptOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border font-bold hover:bg-muted transition-colors text-sm"
            >
              <FileUp className="w-4 h-4" />
              <T>Príjemka</T>
            </button>
          <button
            type="button"
            onClick={() => setEditing({ item: null })}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            <Plus className="w-4 h-4" />
            <T>Nová položka</T>
          </button>
          </div>
        }
      />

      {renaming !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-rename-dialog>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setRenaming(null)}
          />
          <section className="relative w-full max-w-md rounded-2xl bg-background border border-border p-5 space-y-3">
            <h2 className="text-base font-bold">
              <T>Kategórie</T>
            </h2>

            {/* §9.37 - the list this dialog was missing. The categories were only ever visible inside
              * the picker below, so *what categories do I have* cost a click and closed again. Each
              * row fills the picker rather than replacing it: the picker is what the rename flow and
              * its e2e drive, and this is added above it. */}
            <div className="rounded-xl border border-border divide-y divide-border max-h-56 overflow-y-auto">
              {manageableCategories.length === 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  <T>Žiadne kategórie.</T>
                </p>
              )}
              {manageableCategories.map((category, index) => (
                <div
                  key={category.name}
                  data-stock-category={category.name}
                  className="flex items-center justify-between gap-2 px-3 py-2"
                >
                  <span className="text-sm truncate flex-1">
                    {category.name}{' '}
                    <span className="text-xs text-muted-foreground tabular-nums">
                      ({category.count})
                    </span>
                  </span>
                  {category.id !== null && (
                    <>
                      {/* Reordering swaps this row's sort_order with its neighbour's - two writes,
                        * and the list is what the picker and every stock filter read in order. */}
                      <button
                        type="button"
                        data-category-up={category.name}
                        disabled={index === 0 || categoryBusy}
                        aria-label={tt('Posunúť vyššie')}
                        onClick={() => void moveCategory(index, -1)}
                        className="shrink-0 text-xs px-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        data-category-down={category.name}
                        disabled={index === manageableCategories.length - 1 || categoryBusy}
                        aria-label={tt('Posunúť nižšie')}
                        onClick={() => void moveCategory(index, 1)}
                        className="shrink-0 text-xs px-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      >
                        ↓
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    data-rename-this={category.name}
                    onClick={() => setRenaming({ from: category.name, to: '' })}
                    className="shrink-0 text-xs font-bold text-primary hover:underline"
                  >
                    <T>Premenovať</T>
                  </button>
                  {category.id !== null && category.count === 0 && (
                    // Only offered where it can succeed. A category holding items is refused by the
                    // server with the count in the message, and offering a button that always fails
                    // is worse than not offering one.
                    <button
                      type="button"
                      data-category-delete={category.name}
                      disabled={categoryBusy}
                      onClick={() => void removeCategory(category.id!)}
                      className="shrink-0 text-xs font-bold text-destructive hover:underline disabled:opacity-40"
                    >
                      <T>Zmazať</T>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* This said there could be no *Add*, because `stock_items.category` was a string per
              * row: a category existed only while an item carried it, so an empty one had nowhere to
              * live. §10A.16 made it a table and that stopped being true, so the button exists and
              * the note says what is now the case rather than being quietly deleted. */}
            <div className="flex gap-2">
              <input
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                placeholder={tt('Nová kategória')}
                aria-label={tt('Nová kategória')}
                data-category-new
                className="flex-1 min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
              />
              <button
                type="button"
                data-category-add
                disabled={categoryBusy || newCategory.trim() === ''}
                onClick={() => void addCategory()}
                className="shrink-0 min-h-[44px] px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 disabled:opacity-50"
              >
                <T>Pridať</T>
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground" data-category-model-note>
              <T>
                Kategóriu možno pridať aj prázdnu; zmazať sa dá iba tá, ktorá neobsahuje položky.
              </T>
            </p>

            <select
              value={renaming.from}
              onChange={(event) => setRenaming({ ...renaming, from: event.target.value })}
              aria-label={tt('Kategória')}
              data-rename-from
              className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
            >
              <option value="">{tt('- vyberte -')}</option>
              {categoriesOf(items).map((category) => (
                <option key={category.name} value={category.name}>
                  {category.name} ({category.count})
                </option>
              ))}
            </select>
            <input
              value={renaming.to}
              onChange={(event) => setRenaming({ ...renaming, to: event.target.value })}
              placeholder={tt('Nový názov')}
              aria-label={tt('Nový názov')}
              data-rename-to
              className="w-full min-h-[44px] rounded-xl bg-card border border-border px-3 text-sm"
            />
            {renaming.from !== '' && renaming.to.trim() !== '' && (
              <p className="text-[11px] text-muted-foreground" data-rename-plan>
                {planNote(
                  items,
                  renaming.from,
                  renaming.to,
                  categoryRowFor(categoryRows ?? [], renaming.from),
                )}
              </p>
            )}
            {renameProblems(renaming.from, renaming.to, items).map((problem) => (
              <p
                key={problem.message}
                className={cn('text-[11px]', problem.blocking ? 'text-destructive' : 'text-amber-500')}
                data-rename-problem
              >
                {problem.message}
              </p>
            ))}
            <button
              type="button"
              data-rename-save
              disabled={
                renameBusy ||
                isBlocking(renameProblems(renaming.from, renaming.to, items)) ||
                // A row with no items is still worth renaming; only the per-item path needs items.
                (categoryRowFor(categoryRows ?? [], renaming.from) === null &&
                  idsToRename(items, renaming.from).length === 0)
              }
              onClick={() => {
                void (async () => {
                  setRenameBusy(true)
                  const categoryId = categoryRowFor(categoryRows ?? [], renaming.from)
                  const affected = idsToRename(items, renaming.from).length
                  const failed: string[] = []
                  let renamed = 0

                  if (categoryId !== null) {
                    // §10A.16. One transaction: the row and every item's text move together, so
                    // there is no half-renamed state left to report or to retry out of.
                    try {
                      await renameCategory.mutateAsync({
                        id: categoryId,
                        data: { name: renaming.to.trim() },
                      })
                      renamed = affected
                    } catch {
                      failed.push(renaming.from)
                    }
                  } else {
                    // A category the table has never seen — typed into the item editor, which still
                    // writes free text. The old per-item path, and the old warning, still apply.
                    for (const id of idsToRename(items, renaming.from)) {
                      try {
                        await renameItem.mutateAsync({ id, data: renamePayload(renaming.to) })
                        renamed += 1
                      } catch {
                        failed.push(items.find((item) => item.id === id)?.name ?? String(id))
                      }
                    }
                  }

                  setRenameBusy(false)
                  setRenaming(null)
                  void queryClientTop.invalidateQueries({ queryKey: getGetStockItemsQueryKey() })
                  void queryClientTop.invalidateQueries({
                    queryKey: getGetStockItemCategoriesQueryKey(),
                  })
                  notify({
                    ...(failed.length === 0 ? {} : { variant: 'destructive' as const }),
                    title: outcomeNote(renamed, failed),
                  })
                })()
              }}
              className={cn('w-full min-h-[48px] rounded-xl', BUTTON_PRIMARY)}
            >
              <T>Premenovať</T>
            </button>
          </section>
        </div>
      )}

      {receiptOpen && (
        <GoodsReceiptDialog
          items={items}
          mayReadPhoto={mayReadPhoto}
          onClose={() => setReceiptOpen(false)}
          onSaved={() => undefined}
        />
      )}

      {/* FB-838, §9.552 - before the strip, not beside it: every figure below is nought on a failure, so a notice
          underneath would sit under four zeros the reader has already believed. The whole strip goes rather than the
          *Kritických* card alone, because the `{n} položiek nemá nastavené minimum` sentence is gated on a count and
          therefore **vanishes** - an omission, and the fourth way this screen says the shelves are fine. */}
      {itemsFailed ? (
        <div
          className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 flex items-start gap-2 text-sm"
          data-stock-totals-error
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-destructive" />
          <p className="text-destructive leading-relaxed">
            <T>
              Stav skladu sa nepodarilo načítať, takže nevieme, čo je pod minimom. Nulové čísla nižšie by boli
              nepravdivé.
            </T>{' '}
            <button
              type="button"
              onClick={() => void refetchItems()}
              data-stock-totals-retry
              className="underline font-semibold"
            >
              <T>Skúsiť znova</T>
            </button>
          </p>
        </div>
      ) : (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-stock-totals>
        <StatCard label={tt('Položiek')} value={String(counts.total)} icon={Package} color="blue" />
        <StatCard
          label={tt('Kritických')}
          value={String(counts.critical)}
          icon={AlertTriangle}
          color={counts.critical > 0 ? 'red' : 'emerald'}
          sub={counts.low > 0 ? `${counts.low} ${tt('nízkych')}` : undefined}
        />
        <StatCard
          label={tt('Hodnota v nákupe')}
          value={formatCents(value.costCents)}
          icon={Package}
          color="violet"
          sub={`${value.costedCount}/${value.total} ${tt('s cenou')}`}
        />
        {/* FB-078 - a margin over items that have both a cost and a price, or none. */}
        <StatCard
          label={tt('Priemerná marža')}
          value={formatPercent(value.marginPercent)}
          icon={Package}
          color={value.marginPercent === null ? 'amber' : 'emerald'}
          sub={
            value.marginPercent === null
              ? undefined
              : `${value.comparableCount}/${value.total} ${tt('porovnateľných')}`
          }
        />
      </div>
      )}

      {caveat && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-margin-caveat>
          <Info className="w-4 h-4 shrink-0 mt-px" />
          {/* Legacy prints 0 %, which reads like an answer, beside a profit of minus
              the whole cost value. */}
          <span>
            {caveat} <T>Hodnota v nákupe je vypočítaná len z položiek, ktoré cenu majú.</T>
          </span>
        </p>
      )}

      {counts.untracked > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{counts.untracked}</strong>{' '}
            <T>
              položiek nemá nastavené minimum, takže sa pre ne nesleduje, kedy sa blížia ku koncu.
            </T>
          </span>
        </p>
      )}

      <TabGroup
        variant="bar"
        tabs={[
          { id: 'items', label: tt('Položky'), count: items.length },
          { id: 'movements', label: tt('Pohyby') },
        ]}
        active={tab}
        onChange={(id) => setTab(id as StockTab)}
      />

      {tab === 'items' ? (
        <>
          <FilterBar
            search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať položku...') }}
            filters={
              <>
                {/* §9.136 - deactivated is not deleted, so there is a way back to them. */}
                <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(event) => setIncludeInactive(event.target.checked)}
                    data-show-inactive
                    className="h-4 w-4 rounded border-border"
                  />
                  <T>Zobraziť neaktívne</T>
                </label>
                <FilterSelect
                  value={typeFilter}
                  label={tt('Typ položky')}
                  onChange={setTypeFilter}
                  options={[
                    { value: 'all', label: tt('Všetky typy') },
                    { value: 'raw_material', label: tt('Surovina') },
                    { value: 'product', label: tt('Produkt') },
                  ]}
                />
                <FilterSelect
                  value={categoryFilter}
                  label={tt('Kategória položky')}
                  onChange={setCategoryFilter}
                  options={[
                    { value: 'all', label: tt('Všetky kategórie') },
                    ...categories.map((category) => ({ value: category, label: category })),
                  ]}
                />
              </>
            }
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <ItemTable
              items={shown}
              margins={margins}
              onEdit={(item) => setEditing({ item })}
              onMove={(item, type) => setMoving({ item, type })}
            />
          )}
        </>
      ) : (
        <>
          <FilterBar
            search={{ value: movSearch, onChange: setMovSearch, placeholder: tt('Hľadať pohyb...') }}
            filters={
              <>
                <FilterSelect
                  value={movType}
                  label={tt('Typ pohybu')}
                  onChange={setMovType}
                  options={[
                    { value: 'all', label: tt('Všetky pohyby') },
                    { value: 'in', label: tt('Príjem') },
                    { value: 'out', label: tt('Výdaj') },
                    { value: 'adjustment', label: tt('Korekcia') },
                    { value: 'waste', label: tt('Odpis') },
                  ]}
                />
                <FilterSelect
                  value={movCategory}
                  label={tt('Kategória pohybu')}
                  onChange={setMovCategory}
                  options={[
                    { value: 'all', label: tt('Všetky kategórie') },
                    ...categories.map((category) => ({ value: category, label: category })),
                  ]}
                />
                <DateRangePicker
                  from={movFrom}
                  to={movTo}
                  onFromChange={setMovFrom}
                  onToChange={setMovTo}
                />
              </>
            }
          />

          {movementsFailed ? (
            /* **It replaces the table rather than sitting above it**: a banner over a list still reading
             * *Žiadne pohyby* leaves the claim on screen - §3.149's distinction.
             *
             * The cap note goes with it, and that needs no condition: `movementCapNote` is computed from
             * `movementData?.total ?? 0`, which is nought on a failed read, so it is silent anyway. */
            <ReadFailedBlock
              marker="movements"
              title={tt('Pohyby sa nepodarilo načítať')}
              onRetry={() => void refetchMovements()}
            >
              <T>
                Neznamená to, že žiadne nie sú - sklad ich má cez sto tisíc. Skúste to znova.
              </T>
            </ReadFailedBlock>
          ) : movementsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <>
              {movementNote !== '' && (
                <p className="text-[11px] font-semibold text-amber-500" data-movements-capped>
                  {movementNote}
                </p>
              )}
              <MovementTable movements={movements} />
            </>
          )}
        </>
      )}

      {editing && (
        <ItemEditor
          item={editing.item}
          categories={categories}
          onClose={() => setEditing(null)}
        />
      )}
      {moving && (
        <MovementDialog
          item={moving.item}
          type={moving.type}
          onClose={() => setMoving(null)}
        />
      )}
    </div>
  )
}

function ItemTable({
  items,
  margins,
  onEdit,
  onMove,
}: {
  items: StockItem[]
  margins: Map<number, MarginRow>
  onEdit: (item: StockItem) => void
  onMove: (item: StockItem, type: MovementKind) => void
}) {
  const columns: Column<StockItem>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Položka'),
        sortable: true,
        sortValue: (item) => (item.name ?? '').toLowerCase(),
        render: (item) => {
          const level = stockLevel(item)
          return (
            <div className="flex items-center gap-2 min-w-0">
              {level !== 'ok' && (
                <AlertTriangle
                  className={cn(
                    'w-4 h-4 shrink-0',
                    level === 'critical' ? 'text-red-500' : 'text-amber-500',
                  )}
                />
              )}
              <span className="font-semibold truncate">{item.name}</span>
            </div>
          )
        },
      },
      {
        key: 'category',
        header: tt('Kategória'),
        sortable: true,
        sortValue: (item) => item.category ?? '',
        render: (item) => (
          <span className="text-muted-foreground text-xs">{item.category || '-'}</span>
        ),
      },
      {
        key: 'qty',
        header: tt('Množstvo'),
        sortable: true,
        sortValue: (item) => item.qty ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (item) => (
          <span className="font-bold tabular-nums whitespace-nowrap">
            {formatQty(item.qty)} {item.unit}
          </span>
        ),
      },
      {
        key: 'minQty',
        header: tt('Minimum'),
        sortable: true,
        sortValue: (item) => item.minQty ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (item) =>
          (item.minQty ?? 0) > 0 ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatQty(item.minQty)} {item.unit}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground" title={tt('Nesleduje sa')}>
              -
            </span>
          ),
      },
      {
        key: 'level',
        header: tt('Stav'),
        sortable: true,
        sortValue: (item) => stockLevel(item),
        render: (item) => {
          const level = stockLevel(item)
          return <StatusBadge label={levelLabel(level)} color={levelColor(level)} />
        },
      },
      {
        key: 'cost',
        header: tt('Nákupná cena'),
        sortable: true,
        sortValue: (item) => item.costPrice ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (item) => {
          const margin = itemMargin(item, margins)
          if (margin?.effectiveCost == null) {
            return <span className="text-xs text-muted-foreground">-</span>
          }
          return (
            <span className="text-xs tabular-nums whitespace-nowrap">
              {formatRate(margin.effectiveCost)} / {costUnitOf(item) || item.unit}
              {margin.explicitCost == null && (
                <span className="text-muted-foreground"> ({tt('z receptu')})</span>
              )}
            </span>
          )
        },
      },
      {
        key: 'margin',
        header: tt('Marža'),
        sortable: true,
        sortValue: (item) => itemMargin(item, margins)?.marginPct ?? -1,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (item) => {
          const margin = itemMargin(item, margins)
          if (margin?.marginPct == null) {
            return <span className="text-xs text-muted-foreground">-</span>
          }
          return (
            <span
              className={cn(
                'text-xs font-bold tabular-nums',
                margin.marginPct < 0 ? 'text-red-500' : 'text-emerald-500',
              )}
            >
              {formatPercent(margin.marginPct)}
            </span>
          )
        },
      },
      {
        key: 'actions',
        header: '',
        render: (item) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Príjem')} - ${item.name ?? ''}`}
              onClick={() => onMove(item, 'in')}
              tone="emerald"
            >
              <ArrowDownToLine className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${tt('Výdaj')} - ${item.name ?? ''}`}
              onClick={() => onMove(item, 'out')}
              tone="red"
            >
              <ArrowUpFromLine className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton
              label={`${tt('Odpis')} - ${item.name ?? ''}`}
              onClick={() => onMove(item, 'waste')}
              tone="red"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </IconButton>
            <IconButton label={`${tt('Upraviť')} - ${item.name ?? ''}`} onClick={() => onEdit(item)}>
              <RotateCcw className="w-3.5 h-3.5" />
            </IconButton>
          </div>
        ),
      },
    ],
    [margins, onEdit, onMove],
  )

  return (
    <div data-stock-items>
      <DataTable<StockItem>
        columns={columns}
        data={items}
        rowKey={(item) => item.id ?? 0}
        pageSize={25}
        compact
        emptyMessage={tt('Žiadne položky')}
        emptyIcon={<Package className="w-10 h-10 mb-3 opacity-30" />}
      />
    </div>
  )
}

function MovementTable({ movements }: { movements: StockMovement[] }) {
  const columns: Column<StockMovement>[] = useMemo(
    () => [
      {
        key: 'type',
        header: tt('Typ'),
        sortable: true,
        sortValue: (movement) => movementLabel(movement.type),
        render: (movement) => (
          <StatusBadge label={movementLabel(movement.type)} color={movementColor(movement.type)} />
        ),
      },
      {
        key: 'item',
        header: tt('Položka'),
        sortable: true,
        sortValue: (movement) => movement.stockItemName ?? '',
        render: (movement) => (
          <span className="font-semibold text-sm truncate">{movement.stockItemName ?? '-'}</span>
        ),
      },
      {
        key: 'qty',
        header: tt('Množstvo'),
        sortable: true,
        sortValue: signedQty,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (movement) => {
          const qty = signedQty(movement)
          return (
            <span
              className={cn(
                'font-bold tabular-nums whitespace-nowrap',
                qty < 0 ? 'text-red-500' : 'text-emerald-500',
              )}
            >
              {qty > 0 ? '+' : ''}
              {formatQty(qty)} {movement.stockItemUnit ?? ''}
            </span>
          )
        },
      },
      {
        key: 'note',
        header: tt('Poznámka'),
        render: (movement) => (
          // §9.428 - a `DataTable` column has a width, so this stays truncated and gains a `title`:
          // the note is the *reason* a movement happened, and clipping it with no way to read the
          // rest loses that. Reachable rather than unclipped, which is the right answer in a cell.
          <span className="text-xs text-muted-foreground truncate" title={movement.note ?? ''}>
            {movement.note || '-'}
          </span>
        ),
      },
      {
        // §9.136 — its own headed column, as legacy has it. Appended to the note it rendered as a
        // bare `12205`: an unlabelled number that is in fact the order which consumed the stock,
        // with nothing saying so and no way to scan or sort by it.
        key: 'reference',
        header: tt('Referencia'),
        sortable: true,
        sortValue: (movement) => movement.reference ?? '',
        render: (movement) => (
          <span className="text-xs font-mono text-muted-foreground whitespace-nowrap" data-movement-reference>
            {movement.reference || '-'}
          </span>
        ),
      },
      {
        // §9.136 — the stored name, never a live lookup: resolving it against employees now would
        // rename history when somebody's name changes.
        key: 'createdBy',
        header: tt('Užívateľ'),
        sortable: true,
        sortValue: (movement) => movement.createdBy ?? '',
        render: (movement) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-movement-user>
            {movement.createdBy || '-'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: tt('Kedy'),
        sortable: true,
        sortValue: (movement) => new Date(movement.createdAt ?? 0).getTime(),
        render: (movement) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {movement.createdAt ? formatDateTime(movement.createdAt) : '-'}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div data-stock-movements>
      <DataTable<StockMovement>
        columns={columns}
        data={movements}
        rowKey={(movement) => movement.id ?? 0}
        pageSize={25}
        compact
        emptyMessage={tt('Žiadne pohyby')}
      />
    </div>
  )
}

// ─── the editor ──────────────────────────────────────────────────────────────

function ItemEditor({
  item,
  categories,
  onClose,
}: {
  item: StockItem | null
  categories: string[]
  onClose: () => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ItemForm>(() => formOf(item))
  const [newCategory, setNewCategory] = useState('')
  /** The category this typing already means, if it means one — §9.10. */
  const existingMatch = matchingCategory(categories, newCategory)

  const set = <K extends keyof ItemForm>(key: K, value: ItemForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetStockItemsQueryKey() })
    onClose()
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Položku sa nepodarilo uložiť.'),
      variant: 'destructive',
    })

  const create = usePostStockItems({ mutation: { onSuccess: invalidate, onError } })
  const update = usePatchStockItemsId({ mutation: { onSuccess: invalidate, onError } })

  const problem = checkForm(form)
  const pending = create.isPending || update.isPending
  const unitNote = customUnitNote(form.unit)
  const costNote = customUnitNote(form.costUnit)

  const save = () => {
    const payload = itemPayload(form)
    if (item?.id !== undefined) {
      update.mutate({ id: item.id, data: payload })
      return
    }
    create.mutate({ data: payload })
  }

  return (
    <Modal
      title={item ? tt('Upraviť položku') : tt('Nová položka')}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={save}
            disabled={pending || !canSave(problem)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {pending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </>
      }
    >
      <Field label={tt('Názov')}>
        <input
          value={form.name}
          onChange={(event) => set('name', event.target.value)}
          aria-label={tt('Názov položky')}
          className={INPUT}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={tt('Typ')}>
          <select
            value={form.type}
            onChange={(event) => set('type', event.target.value)}
            aria-label={tt('Typ')}
            className={INPUT}
          >
            <option value="raw_material">{tt('Surovina')}</option>
            <option value="product">{tt('Produkt')}</option>
          </select>
        </Field>
        <Field label={tt('Jednotka')} hint={unitNote ?? undefined}>
          <select
            value={form.unit}
            onChange={(event) => set('unit', event.target.value)}
            aria-label={tt('Jednotka')}
            className={INPUT}
          >
            {unitOptionsFor(form.unit).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label={tt('Kategória')}>
        <select
          value={form.category}
          onChange={(event) => set('category', event.target.value)}
          aria-label={tt('Kategória')}
          className={INPUT}
        >
          <option value="">{tt('- Bez kategórie -')}</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <div className="flex gap-2 mt-2">
          <input
            value={newCategory}
            onChange={(event) => setNewCategory(event.target.value)}
            placeholder={tt('Nová kategória...')}
            aria-label={tt('Nová kategória')}
            data-new-category
            className={cn(INPUT, 'flex-1 text-xs')}
          />
          <button
            type="button"
            data-use-category
            disabled={!newCategory.trim()}
            onClick={() => {
              // The existing one if this is that one differently typed, otherwise what was typed.
              // A stock category is a text column, so *Zelenina* and *zelenina* are two categories to
              // the database and one to the person typing — §9.10.
              set('category', matchingCategory(categories, newCategory) ?? newCategory.trim())
              setNewCategory('')
            }}
            className="px-3 rounded-lg border border-border text-xs font-semibold disabled:opacity-40"
          >
            {existingMatch === null ? <T>Použiť</T> : <T>Použiť existujúcu</T>}
          </button>
        </div>
        {existingMatch !== null && (
          <p className="text-[11px] text-amber-600 mt-1" data-category-exists>
            <T>Táto kategória už existuje ako</T> „{existingMatch}" - <T>použije sa tá.</T>
          </p>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={tt('Množstvo')}>
          <input
            value={form.qty}
            onChange={(event) => set('qty', event.target.value)}
            aria-label={tt('Množstvo')}
            className={INPUT}
            placeholder="0"
          />
        </Field>
        <Field label={tt('Minimum')} hint={tt('0 znamená, že sa nesleduje')}>
          <input
            value={form.minQty}
            onChange={(event) => set('minQty', event.target.value)}
            aria-label={tt('Minimum')}
            className={INPUT}
            placeholder="0"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={tt('Nákupná cena')} hint={tt('Za jednotku nižšie')}>
          <input
            value={form.costPrice}
            onChange={(event) => set('costPrice', event.target.value)}
            aria-label={tt('Nákupná cena')}
            className={INPUT}
            placeholder="0.00"
          />
        </Field>
        <Field label={tt('Jednotka pre cenu')} hint={costNote ?? undefined}>
          {/* FB-077: the same custom-value fallback the unit select above has. */}
          <select
            value={form.costUnit || form.unit}
            onChange={(event) => set('costUnit', event.target.value)}
            aria-label={tt('Jednotka pre cenu')}
            className={INPUT}
          >
            {unitOptionsFor(form.costUnit || form.unit).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {problem?.kind === 'cost-unit-mismatch' && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-cost-unit-mismatch>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            <T>Cena je zadaná za</T> <strong>{problem.costUnit}</strong>,{' '}
            <T>ale položka sa počíta v</T> <strong>{problem.unit}</strong>.{' '}
            <T>Prepočet nikto nerobí - uistite sa, že to tak má byť.</T>
          </span>
        </p>
      )}

      {problem && problem.kind !== 'cost-unit-mismatch' && (
        <p className="text-xs text-red-500">
          {problem.kind === 'no-name' && <T>Názov je povinný.</T>}
          {problem.kind === 'no-unit' && <T>Jednotka je povinná.</T>}
          {problem.kind === 'negative-qty' && <T>Množstvo ani minimum nemôžu byť negatívne.</T>}
        </p>
      )}

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={form.active}
          onChange={(event) => set('active', event.target.checked)}
          className="w-4 h-4 rounded"
        />
        <span className="text-sm">
          <T>Aktívna položka</T>
        </span>
      </label>
    </Modal>
  )
}

function MovementDialog({
  item,
  type,
  onClose,
}: {
  item: StockItem
  type: MovementKind
  onClose: () => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<MovementForm>({
    stockItemId: item.id ?? 0,
    type,
    qty: '',
    note: '',
    reference: '',
  })
  const [confirming, setConfirming] = useState(false)

  const post = usePostStockMovements({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetStockItemsQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetStockMovementsQueryKey() })
        onClose()
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Pohyb sa nepodarilo zapísať.'),
          variant: 'destructive',
        }),
    },
  })

  const warning = overdrawWarning(item, form)
  const submit = () => {
    if (warning) {
      setConfirming(true)
      return
    }
    post.mutate({ data: movementPayload(form) })
  }

  return (
    <>
      <Modal
        title={`${movementLabel(type)} - ${item.name ?? ''}`}
        onClose={onClose}
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={post.isPending || !canPost(form)}
              className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {post.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Zapísať</T>}
            </button>
          </>
        }
      >
        <p className="text-xs text-muted-foreground">
          <T>Na sklade je</T>{' '}
          <strong className="text-foreground">
            {formatQty(item.qty)} {item.unit}
          </strong>
        </p>

        <Field label={`${tt('Množstvo')} (${item.unit ?? ''})`}>
          <input
            value={form.qty}
            onChange={(event) => setForm((current) => ({ ...current, qty: event.target.value }))}
            aria-label={tt('Množstvo pohybu')}
            className={INPUT}
            placeholder="0"
          />
        </Field>

        {/* Legacy posts and then shows the new figure. This says it first. */}
        {canPost(form) && (
          <p className="text-xs text-muted-foreground" data-resulting-qty>
            <T>Po zápise bude na sklade</T>{' '}
            <strong className={cn(resultingQty(item, form) < 0 && 'text-red-500')}>
              {formatQty(resultingQty(item, form))} {item.unit}
            </strong>
          </p>
        )}

        {warning && canPost(form) && (
          <p className="flex items-start gap-1.5 text-xs text-amber-500" data-overdraw-warning>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            {warning}
          </p>
        )}

        <Field label={tt('Poznámka')}>
          <input
            value={form.note}
            onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
            aria-label={tt('Poznámka k pohybu')}
            className={INPUT}
            placeholder={tt('Napr. faktúra 2026/119')}
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false)
          post.mutate({ data: movementPayload(form) })
        }}
        title={tt('Zapísať pohyb do minusu?')}
        description={`${warning ?? ''} ${tt('Na sklade je')} ${formatQty(item.qty)} ${item.unit ?? ''}.`}
        confirmLabel={tt('Zapísať')}
        variant="danger"
      />
    </>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Modal({
  title,
  onClose,
  footer,
  children,
}: {
  title: string
  onClose: () => void
  footer: ReactNode
  children: ReactNode
}) {
  /* §9.494, `TODO.md` 3f - **the third and fourth copies of this component** (§9.491 found two and the real
     number is five; `TODO.md` carries the extraction). Its close is unconditional because a generic `Modal`
     takes no busy flag and its wrapper already calls `onClose` on a backdrop click with no guard, so Escape
     makes the same promise - §9.491's reasoning, now applied to its own siblings. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">{children}</div>
        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">{footer}</div>
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/80">{hint}</p>}
    </div>
  )
}


/** A quantity, not money: `8,25` and `4`, never `4,00`. */
function formatQty(qty: number | undefined): string {
  const value = qty ?? 0
  const rounded = Math.round(value * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

/** A per-unit price keeps its decimals — 1,499 €/kg is what a supplier quotes. */
function formatRate(rate: number): string {
  return `${String(Math.round(rate * 1000) / 1000).replace('.', ',')} €`
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
